import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import cx from 'clsx';
// import { useHistory } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';

import {
  HelperTooltip,
  UserAvatar,
  FollowButton,
  openUserDealModal,
  UserCardPopup,
} from 'components';
import { CopyImage } from 'components/shared';
import { useSwitch, useTheme, useUserDetail } from 'hooks';
import { IconLoadingAvatar, IconLoadingAvatarDark } from '@/assets/icon/avatar';
import { getUserNameByUserInfo, isSybilByUserDesc, openNewWindow } from '@/utils';
// import { numFormat } from '@/utils';
import { UserCardShape } from '../../hooks';
import style from './index.module.css';
import { useHiUserCache } from 'hooks';
import HiOperateMenu, { HiOperateMenuType } from '../HiOperateMenu';
import { HiSession } from '../../types';
import MemoEditModal from '@/components/UserTag/MemoEditModal';
import { IconRcAccountCommentIcon } from '@/assets';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';

type HeaderUserInfoProps = {
  address: string;
  hiSession?: HiSession;
  hideOperationMenu?: boolean;
  userCardInfo: UserCardShape;
  loadingStatus: { userInfo: boolean };
  fetchingStatus: { userInfo: boolean };
};

export const HeaderUserInfo = ({
  userCardInfo,
  address,
  hiSession,
  hideOperationMenu,
}: HeaderUserInfoProps) => {
  const { t } = useTranslation();

  const sessionId = hiSession?.id;
  const { userDict } = useHiUserCache();
  const {
    on: isMemoEditModalShow,
    turnOn: turnOnMemoEditModal,
    turnOff: turnOffMemoEditModal,
  } = useSwitch(false);
  const { isDark } = useTheme();
  const LoadingAvatar = useMemo(
    () => (isDark ? IconLoadingAvatarDark : IconLoadingAvatar),
    [isDark],
  );
  const { data: addrInfo, refetch: refetchAddrInfo } = useUserDetail(address);

  const userInfo = useMemo(
    () => (userDict[address] as UserInfo) || userCardInfo.userInfo,
    [address, userCardInfo.userInfo, userDict],
  );
  // const history = useHistory();

  // const hiInfoList = useMemo(() => {
  //   return [
  //     {
  //       key: t('HiConversation.muhike'),
  //       value: numFormat((userCardInfo.offerPrice || 0) / 1e4, 2, '$'),
  //       tooltip: (
  //         <>
  //           {t('HeaderUserInfo.1gdcztm')}{' '}
  //           <div className={style.gotoLink} onClick={() => history.push('/hi-whitepaper')}>
  //             {t('HeaderUserInfo.ao03h6')}
  //           </div>
  //         </>
  //       ),
  //     },
  //     {
  //       key: t('HeaderUserInfo.unanswered-offer-value'),
  //       value: numFormat((userCardInfo.unchargedOfferValue || 0) / 1e4, 2, '$'),
  //       tooltip: t('HeaderUserInfo.unansweredTip'),
  //     },
  //     // {
  //     //   key: 'Response rate',
  //     //   value: userCardInfo.repliedRate
  //     //     ? `${(userCardInfo.repliedRate * 100).toFixed(0)}%`
  //     //     : '100%',
  //     //   tooltip: 'The percentage of offers getting replies from the user',
  //     // },
  //   ];
  // }, [history, userCardInfo.offerPrice, userCardInfo.unchargedOfferValue, t]);

  const isSuicide = useMemo(
    () => userCardInfo?.userInfo?.is_suicide,
    [userCardInfo?.userInfo?.is_suicide],
  );

  const handleMemoNameChange = (value: string) => {
    refetchAddrInfo(); // 刷新popover中数据
  };

  const openAddressLabelModal = useCallback(() => {
    openUserDealModal({
      address,
      refetchCallback: () => {
        refetchAddrInfo();
      },
    });
  }, [address, refetchAddrInfo]);

  return (
    <div className={style.headerInfo}>
      <div className={style.userInfo}>
        <div className={style.userInfoMain}>
          {userCardInfo.loadingStatus.userInfo ? (
            <LoadingAvatar className={style.avatar} height={50} width={50}></LoadingAvatar>
          ) : (
            <span
              className={style.userAvatarWrapper}
              onClick={() => openNewWindow(`/profile/${address}`, '_blank', true)}>
              <UserAvatar
                userCardPopup={{
                  hideSayHiButton: true,
                }}
                className={style.avatar}
                size={50}
                data={userInfo?.desc}
              />
            </span>
          )}
          <div className={style.userInfoContent}>
            <div className={style.uid}>
              {userInfo ? (
                <>
                  <span
                    onClick={() => {
                      openNewWindow(`/profile/${address}`, '_blank');
                    }}
                    className={cx(
                      style.accountName,
                      userInfo?.desc?.user?.web3_id && style.hasAccount,
                    )}>
                    <UserCardPopup address={address} className={style.userInfoText} hideSayHiButton>
                      {isSuicide ? (
                        <SuicideTag />
                      ) : (
                        userInfo?.desc?.user?.web3_id || t('HeaderInfo.18vjcq')
                      )}
                    </UserCardPopup>
                  </span>
                  <span className={cx(style.comment)} onClick={turnOnMemoEditModal}>
                    <IconRcAccountCommentIcon />
                    {userInfo?.desc?.user?.memo ? (
                      <span className={style.commentText}>{userInfo?.desc?.user?.memo}</span>
                    ) : null}
                  </span>

                  {isSybilByUserDesc(userInfo?.desc) ? (
                    <HelperTooltip
                      inParent
                      title={
                        <>
                          {t('HeaderUserInfo.ehrrk3')}{' '}
                          <span
                            className={'db-user-tag-tooltip-link-text'}
                            onClick={openAddressLabelModal}>
                            {t('HeaderUserInfo.vn1jki')}
                          </span>
                        </>
                      }>
                      <div className={style.sybilLabel}>{t('HeaderUserInfo.1bu9xb')}</div>
                    </HelperTooltip>
                  ) : null}

                  {/* <RankingTag address={address} className={style.rankingTag} disableRankPop /> */}

                  <FollowButton className={style.followBtn} userInfo={addrInfo} address={address} />
                </>
              ) : (
                <Skeleton width={264} height={24}></Skeleton>
              )}
            </div>

            <div className={style.address}>
              {address ? (
                <>
                  <span className={style.addressText}>{address}</span>
                  <HelperTooltip title={t('UserCardPopup.copy')} placement="top">
                    <div className={style.icon}>
                      <CopyImage value={address} />
                    </div>
                  </HelperTooltip>
                </>
              ) : (
                <Skeleton width={264} height={20}></Skeleton>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={style.headerRight}>
        {/* <div className={style.hiOfferInfo}>
          <div className={style.offerInfoKey}>
            {hiInfoList.map(item => {
              return userCardInfo.loadingStatus.userInfo ? (
                <Skeleton key={item.key} className={style.key} width={148} height={14}></Skeleton>
              ) : (
                <HelperTooltip
                  key={item.key}
                  title={item.tooltip}
                  placement="bottom"
                  destroyTooltipOnHide={false}>
                  <span className={style.key}>{item.key}</span>
                </HelperTooltip>
              );
            })}
          </div>
          <div className={style.offerInfValue}>
            {!userCardInfo.loadingStatus.userInfo &&
              hiInfoList.map((item, i) => (
                <span key={i} className={style.value}>
                  {item.value}
                </span>
              ))}
          </div>
        </div> */}
        {hideOperationMenu ? null : (
          <div className={style.operateMenuWrapper}>
            <HiOperateMenu
              config={{
                sessionId: sessionId!,
                otherSideHiId: address,
                otherSideName: getUserNameByUserInfo(userInfo, false) || '',
                isBlocked: userInfo?.desc?.is_block,
              }}
              menuTypeList={
                hiSession
                  ? [
                      hiSession?.is_pinned ? HiOperateMenuType.UNPIN : HiOperateMenuType.PIN,
                      HiOperateMenuType.ARCHIVE,
                      HiOperateMenuType.ADDRESS_LABEL,
                      HiOperateMenuType.REPORT,
                    ]
                  : []
              }
            />
          </div>
        )}
      </div>
      {isMemoEditModalShow && (
        <MemoEditModal
          prevMemo={addrInfo?.desc?.user?.memo}
          success={handleMemoNameChange}
          addr={address}
          onClose={turnOffMemoEditModal}
        />
      )}
    </div>
  );
};

export default HeaderUserInfo;
